import { chakra } from '@chakra-ui/react';
import { forwardRef, useId, type ReactNode } from 'react';

const Bar = chakra('div', {
  base: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '3',
    minWidth: 0,
  },
});

const Field = chakra('div', {
  base: { display: 'flex', flexDirection: 'column', gap: '1', minWidth: 0 },
});

const Label = chakra('label', {
  base: {
    fontFamily: 'body',
    fontSize: 'xs',
    fontWeight: 'medium',
    color: 'text.muted',
    letterSpacing: 'wide',
  },
});

const Spacer = chakra('div', { base: { flex: '1 1 auto' } });

const TrailingActions = chakra('div', {
  base: { display: 'flex', alignItems: 'center', gap: '2', flexShrink: 0 },
});

export interface FilterBarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  children: ReactNode;
  /** Right-aligned actions (a "Clear filters" button, a toggle). */
  actions?: ReactNode;
}

/**
 * A horizontal bar of filter controls (§ operations). Groups labelled filter fields on
 * the left and optional actions (clear, toggles) on the right; wraps gracefully on
 * narrow screens. Pair with {@link FilterField} to label each control consistently.
 */
export const FilterBar = forwardRef<HTMLDivElement, FilterBarProps>(function FilterBar(
  { children, actions, ...rest },
  ref,
) {
  return (
    <Bar ref={ref} role="search" {...rest}>
      {children}
      {actions && (
        <>
          <Spacer />
          <TrailingActions>{actions}</TrailingActions>
        </>
      )}
    </Bar>
  );
});

export interface FilterFieldProps {
  label: ReactNode;
  /** Render-prop receives the id to wire onto the control (`htmlFor` ↔ `id`). */
  children: (props: { id: string }) => ReactNode;
  /** Hide the visual label (keeps it as the control's accessible name via aria). */
  hideLabel?: boolean;
}

/** A labelled filter control — a small caption above the control. */
export function FilterField({ label, children, hideLabel }: FilterFieldProps) {
  const id = useId();
  return (
    <Field>
      {!hideLabel && (
        <Label htmlFor={id} id={`${id}-label`}>
          {label}
        </Label>
      )}
      {children({ id })}
    </Field>
  );
}
